import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import Card from '../ui/Card'
import EditingReturnBanner from '../projects/EditingReturnBanner'
import { useFieldFocusHandler } from '../../hooks/useFieldFocusHandler'
import Badge from '../ui/Badge'
import Button from '../ui/Button'
import PageHeader from '../layout/PageHeader'
import InlineSelect from '../ui/InlineSelect'
import { useProjects } from '../../contexts/ProjectsContext'
import { useWpsSections } from '../../contexts/WpsSectionsContext'
import PageLoader from '../ui/PageLoader'

type WeldTimesPayload = {
  root: string
  fill: string
  cap: string
  total: string
  maxRootToHot: string
  maxHotToFill: string
  maxRootToHotUnit: string
  maxHotToFillUnit: string
  minWeldersRoot: string
  minWeldersRemaining: string
}

const EMPTY_WELD_TIMES: WeldTimesPayload = {
  root: '',
  fill: '',
  cap: '',
  total: '',
  maxRootToHot: '',
  maxHotToFill: '',
  maxRootToHotUnit: 'Hours',
  maxHotToFillUnit: 'Hours',
  minWeldersRoot: '',
  minWeldersRemaining: ''
}

const TIME_LIMIT_SENTINEL = '-'

function isSpecifiedTimeLimit(value: string): boolean {
  const trimmed = value.trim()
  return trimmed.length > 0 && trimmed !== TIME_LIMIT_SENTINEL
}

function serializeWeldTimes(p: WeldTimesPayload): string {
  return JSON.stringify(p)
}

export default function WpsWeldTimes() {
  const { id, pipeId } = useParams()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const previewReturnTo = searchParams.get('returnTo')
  const { getProject, loading: projectsLoading } = useProjects()
  const { getSection, updateSection, loading: sectionsLoading, sections } = useWpsSections()
  const project = id ? getProject(id) : undefined
  const pipeSet = project?.pipeSets.find((pipe) => pipe.id === pipeId)
  useFieldFocusHandler({
    ready: !sectionsLoading,
    resolveTarget: (params) => {
      if (!params.focusField) return null
      const el = document.querySelector(
        `[data-focus-field="${params.focusField}"]`
      ) as HTMLElement | null
      if (!el) return null
      const inner = el.querySelector(
        'input, select, textarea, button, [role="button"]'
      ) as HTMLElement | null
      return inner ?? el
    }
  })

  const [root, setRoot] = useState('')
  const [fill, setFill] = useState('')
  const [cap, setCap] = useState('')
  const [total, setTotal] = useState('')
  const [maxRootToHot, setMaxRootToHot] = useState('')
  const [maxHotToFill, setMaxHotToFill] = useState('')
  const [maxRootToHotUnit, setMaxRootToHotUnit] = useState('Hours')
  const [maxHotToFillUnit, setMaxHotToFillUnit] = useState('Hours')
  const [minWeldersRoot, setMinWeldersRoot] = useState('')
  const [minWeldersRemaining, setMinWeldersRemaining] = useState('')
  const [specifyTimeLimits, setSpecifyTimeLimits] = useState(false)
  const [savedWeldTimesJson, setSavedWeldTimesJson] = useState('')
  const [saving, setSaving] = useState(false)

  const weldTimesPayload = useMemo(
    () =>
      serializeWeldTimes({
        root,
        fill,
        cap,
        total,
        maxRootToHot: specifyTimeLimits ? maxRootToHot : TIME_LIMIT_SENTINEL,
        maxHotToFill: specifyTimeLimits ? maxHotToFill : TIME_LIMIT_SENTINEL,
        maxRootToHotUnit,
        maxHotToFillUnit,
        minWeldersRoot,
        minWeldersRemaining
      }),
    [
      root,
      fill,
      cap,
      total,
      maxRootToHot,
      maxHotToFill,
      maxRootToHotUnit,
      maxHotToFillUnit,
      minWeldersRoot,
      minWeldersRemaining,
      specifyTimeLimits
    ]
  )

  const isDirty = useMemo(
    () => savedWeldTimesJson !== '' && weldTimesPayload !== savedWeldTimesJson,
    [weldTimesPayload, savedWeldTimesJson]
  )

  useEffect(() => {
    if (!pipeId || sectionsLoading) return
    const parsed = getSection('weldTimes')
    try {
      if (parsed) {
        const next: WeldTimesPayload = {
          root: parsed.root || '',
          fill: parsed.fill || '',
          cap: parsed.cap || '',
          total: parsed.total || '',
          maxRootToHot: parsed.maxRootToHot || '',
          maxHotToFill: parsed.maxHotToFill || '',
          maxRootToHotUnit: parsed.maxRootToHotUnit || 'Hours',
          maxHotToFillUnit: parsed.maxHotToFillUnit || 'Hours',
          minWeldersRoot: parsed.minWeldersRoot || '',
          minWeldersRemaining: parsed.minWeldersRemaining || ''
        }
        setRoot(next.root)
        setFill(next.fill)
        setCap(next.cap)
        setTotal(next.total)
        const hasLimits =
          isSpecifiedTimeLimit(next.maxRootToHot) ||
          isSpecifiedTimeLimit(next.maxHotToFill)
        setSpecifyTimeLimits(hasLimits)
        setMaxRootToHot(hasLimits ? next.maxRootToHot : '')
        setMaxHotToFill(hasLimits ? next.maxHotToFill : '')
        setMaxRootToHotUnit(next.maxRootToHotUnit)
        setMaxHotToFillUnit(next.maxHotToFillUnit)
        setMinWeldersRoot(next.minWeldersRoot)
        setMinWeldersRemaining(next.minWeldersRemaining)
        setSavedWeldTimesJson(
          serializeWeldTimes({
            ...next,
            maxRootToHot: hasLimits ? next.maxRootToHot : TIME_LIMIT_SENTINEL,
            maxHotToFill: hasLimits ? next.maxHotToFill : TIME_LIMIT_SENTINEL
          })
        )
      } else {
        setRoot('')
        setFill('')
        setCap('')
        setTotal('')
        setMaxRootToHot('')
        setMaxHotToFill('')
        setMaxRootToHotUnit('Hours')
        setMaxHotToFillUnit('Hours')
        setMinWeldersRoot('')
        setMinWeldersRemaining('')
        setSpecifyTimeLimits(false)
        setSavedWeldTimesJson(
          serializeWeldTimes({
            ...EMPTY_WELD_TIMES,
            maxRootToHot: TIME_LIMIT_SENTINEL,
            maxHotToFill: TIME_LIMIT_SENTINEL
          })
        )
      }
    } catch {
      // ignore
    }
  }, [pipeId, sectionsLoading, getSection, sections])

  const handleSave = async () => {
    if (!pipeId || !isDirty || saving) return
    const body = {
      root,
      fill,
      cap,
      total,
      maxRootToHot: specifyTimeLimits ? maxRootToHot : TIME_LIMIT_SENTINEL,
      maxHotToFill: specifyTimeLimits ? maxHotToFill : TIME_LIMIT_SENTINEL,
      maxRootToHotUnit,
      maxHotToFillUnit,
      minWeldersRoot,
      minWeldersRemaining
    }
    setSaving(true)
    try {
      await updateSection('weldTimes', body)
      setSavedWeldTimesJson(serializeWeldTimes(body))
      toast.success('Weld times saved')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not save weld times')
    } finally {
      setSaving(false)
    }
  }

  if (id && projectsLoading) {
    return <PageLoader label="Loading project…" />
  }

  if (!project || !pipeSet) {
    return (
      <Card title="Weld Times not found">
        <p className="text-sm text-gray-500">Return to the Project Hub to continue.</p>
        <div className="mt-4">
          <Link to="/resources">
            <Button>Go to Project Hub</Button>
          </Link>
        </div>
      </Card>
    )
  }

  if (sectionsLoading || savedWeldTimesJson === '') {
    return <PageLoader label="Loading weld times…" />
  }

  return (
    <div className="space-y-6">
      <EditingReturnBanner returnTo={previewReturnTo} contextLabel="Weld Times" />
      <PageHeader
        backTo={{ onClick: () => navigate(-1), label: 'Back' }}
        title={
          <>
            <span>Weld Times</span>
            <span className="text-heritage-grey/60">•</span>
            <span className="truncate text-text-grey">{project.name}</span>
          </>
        }
        subtitle={
          <>
            <span>{(project.endClients?.[0] ?? project.client)} • {project.location}</span>
            <Badge tone="primary">
              {pipeSet.nominalOd} {pipeSet.nominalOdUnit} × {pipeSet.wallThickness}{' '}
              {pipeSet.wallThicknessUnit}
            </Badge>
          </>
        }
        actions={
          <>
            <Button disabled={!isDirty || saving} loading={saving} onClick={() => void handleSave()}>
              Save
            </Button>
          </>
        }
      />

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <p className="text-xs font-semibold text-gray-700">Time Limits Between Passes</p>
        <label className="mt-3 inline-flex items-center gap-2 text-xs text-gray-600">
          <input
            type="checkbox"
            className="h-3.5 w-3.5 rounded border-gray-300 text-blueprint-blue focus:ring-blueprint-blue"
            checked={specifyTimeLimits}
            onChange={(e) => {
              const checked = e.target.checked
              setSpecifyTimeLimits(checked)
              if (!checked) {
                setMaxRootToHot('')
                setMaxHotToFill('')
              }
            }}
            data-testid="weld-times-specify-limits"
          />
          Specify time limits between passes
        </label>
        {specifyTimeLimits ? (
          <div className="mt-3 grid gap-4 md:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-gray-500">
                Max Time between Root & Hot Pass
              </label>
              <div className="mt-1 flex rounded-md border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500 divide-x divide-gray-200">
                <input
                  className="w-full border-0 bg-transparent px-3 py-2 text-sm focus:outline-none"
                  value={maxRootToHot}
                  onChange={(e) => setMaxRootToHot(e.target.value)}
                  type="number"
                  min={0}
                  placeholder="24"
                />
                <InlineSelect
                  value={maxRootToHotUnit}
                  options={[
                    { value: 'Mins', label: 'Mins' },
                    { value: 'Hours', label: 'Hours' }
                  ]}
                  onChange={setMaxRootToHotUnit}
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">
                Max Time between Hot Pass & Fill 1
              </label>
              <div className="mt-1 flex rounded-md border border-gray-300 shadow-sm focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-500 divide-x divide-gray-200">
                <input
                  className="w-full border-0 bg-transparent px-3 py-2 text-sm focus:outline-none"
                  value={maxHotToFill}
                  onChange={(e) => setMaxHotToFill(e.target.value)}
                  type="number"
                  min={0}
                  placeholder="24"
                />
                <InlineSelect
                  value={maxHotToFillUnit}
                  options={[
                    { value: 'Mins', label: 'Mins' },
                    { value: 'Hours', label: 'Hours' }
                  ]}
                  onChange={setMaxHotToFillUnit}
                />
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <p className="text-xs font-semibold text-gray-700">Minimum Welder Counts</p>
        <div className="mt-3 grid gap-4 md:grid-cols-2">
          <div>
            <label className="text-xs font-medium text-gray-500">
              Min. # of welders root pass
            </label>
            <input
              className="input-primary mt-1"
              value={minWeldersRoot}
              onChange={(e) => setMinWeldersRoot(e.target.value)}
              type="number"
              min={0}
              placeholder="1"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">
              Min. # of welders remaining
            </label>
            <input
              className="input-primary mt-1"
              value={minWeldersRemaining}
              onChange={(e) => setMinWeldersRemaining(e.target.value)}
              type="number"
              min={0}
              placeholder="2"
            />
          </div>
        </div>
      </div>
    </div>
  )
}
